import { CommonModule } from '@angular/common';
import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { AuthService } from '@app/auth.service';
import { ILoginParams, ILoginResponse } from '@app/lib/models/login.model';
import { DialogService } from '@app/lib/services/dialog.service';
import { VersionService } from '@app/lib/services/version.service'; // Import VersionService
import { ResetPasswordComponent } from './reset-password/reset-password.component';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    ReactiveFormsModule,
    MatIconModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit {

  public toggleShowPwd: WritableSignal<boolean> = signal(false);
  version: string | null = null;
  buildTime: string | null = null;
  formattedBuildTime: string = '';
  public isLoading: WritableSignal<boolean> = signal(false);

  constructor(
    private dialog: MatDialog,
    private authService: AuthService,
    private dialogService: DialogService,
    private router: Router,
    private versionService: VersionService // Inject the service here
  ) { }

  loginForm: FormGroup = new FormGroup({
    username: new FormControl('', [
      Validators.required,
      Validators.minLength(7)
    ]),
    password: new FormControl('', Validators.required)
  });

  ngOnInit(): void {
    if (this.authService.getData()) {
      const token = this.authService.getData()?.token as string;
      if (token && this.authService.isTokenExpired(token)) {
        localStorage.removeItem('userData');
        this.router.navigate(['/login']);
      } else {
        this.router.navigate(['/dashboard']);
      }
    }
    // Fetch version data when the component initializes
    this.versionService.getVersion().subscribe({
      next: (data: any) => {
        this.version = data.version;
        this.buildTime = data.buildTime;

      },
      error: (err: any) => {
        console.error('Error fetching version:', err);
      }
    });

  }

  formatBuildTime(): void {
    if (this.buildTime) {
      const date = new Date(this.buildTime);
      this.formattedBuildTime = date.toLocaleString(); // Customize this format as needed
    }
  }

  public togglePwd() {
    this.toggleShowPwd.update((val: boolean) => !val);
  }
  onForgotPassword() {
    // Navigate to forgot password screen or show dialog
    this.router.navigate(['/forgot-password']); // or open dialog
  }

  onSubmit(): void {
    this.isLoading.set(true);
    if (this.loginForm.invalid) {
      const usernameErrors = this.loginForm.get('username')?.errors;
      const passwordErrors = this.loginForm.get('password')?.errors;

      let errorMessage = '';

      if (usernameErrors?.['required']) {
        errorMessage += 'Username is required.<br>';
      } else if (usernameErrors?.['minlength']) {
        errorMessage += 'Username must be a minimum of 8 characters long.';
      }

      if (passwordErrors?.['required']) {
        errorMessage += 'Password is required.<br>';
      }

      if (errorMessage) {
        this.dialogService.openDialog1({
          data: {
            title: 'Error',
            message: errorMessage,
            icon: 'warning'
          }, disableClose: true,           // Prevents closing on outside click or ESC
          hasBackdrop: true,            // Shows backdrop to block interaction
          backdropClass: 'dialog-backdrop', // Optional: custom styling
        });
      }

      return; // Stop if form is invalid
    }
    this.isLoading.set(true);
    const formData = this.loginForm.getRawValue();
    const reqBody: ILoginParams = { loginUsername: formData.username, loginPassword: formData.password };
    this.authService.validateUser(reqBody).subscribe({
      next: (res: ILoginResponse | string) => {
        this.isLoading.set(false);
        if (res && typeof res === 'string') {
          const raw = (res || '').trim();

          // known deny message
          if (/ACCESS\s+IS\s+NOT[-\s]?GRANTED/i.test(raw)) {
            this.dialogService.openDialog1({
              data: { title: 'Error', message: 'User ID or Password is incorrect', icon: 'error' },
              disableClose: true, hasBackdrop: true, backdropClass: 'dialog-backdrop',
            });
            return;
          }
          else if (res) {
            const raw = JSON.parse(res) as ILoginResponse;
            if (raw.loginData) {
              if (raw.loginData.failedLoginAttempts) {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Error',
                    message: 'Your account has been locked because of too many invalid login attempts. Please contact Admin for password reset.',
                    icon: 'error', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              }
              else if (!raw.loginData.passwordValid) {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Error',
                    message: 'User ID or Password is incorrect',
                    icon: 'error', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              }
              else if (!raw.loginData.passwordActive) {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Error',
                    message: 'Your password has been expired, Please contact Admin to reset',
                    icon: 'error', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              } else if (raw.loginData.noOfDaysToExpiryPassword <= raw.loginData.passwordExpireDays) {
                this.authService.setData({ ...raw.loginData, userName: formData.username, token: raw.token, refreshToken: raw.refreshToken });
                this.authService.user.next({ ...raw.loginData, userName: formData.username, token: raw.token, refreshToken: raw.refreshToken });
                const dialogRef = this.dialogService.openDialog1({
                  data: {
                    title: 'Information',
                    message: `Your password will expire in ${raw.loginData.noOfDaysToExpiryPassword} day(s). Please update it soon.`,
                    icon: 'warning',
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });

                dialogRef.afterClosed().subscribe(() => {
                  // Perform another action here after closing the dialog
                  this.router.navigate(['/dashboard']);

                });
              } else if (raw.loginData.newUser) {
                this.router.navigate(['/reset-password']);
                this.authService.setData({ ...raw.loginData, userName: formData.username, token: raw.token, refreshToken: raw.refreshToken });

              } else if (!raw.loginData.userActive) {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Error',
                    message: 'Your user account has been expired, Please contact Admin',
                    icon: 'error', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              }
              else {
                this.authService.setData({ ...raw.loginData, userName: formData.username, token: raw.token, refreshToken: raw.refreshToken });
                this.authService.user.next({ ...raw.loginData, userName: formData.username, token: raw.token, refreshToken: raw.refreshToken });
                this.router.navigate(['/dashboard']);

              }
            } else if (raw.data) {
              if (raw.data.failedLoginAttempts) {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Information',
                    message: "Your account has been locked because of too many invalid login attempts. Please contact Admin for password reset.",
                    icon: 'info', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              } else if (raw.data.userAlreadyLoggedInOtherLocation) {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Error',
                    message: raw.data.userAlreadyLoggedInOtherLocation,
                    icon: 'error', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              } else {
                this.dialogService.openDialog1({
                  data: {
                    title: 'Error',
                    message: 'User ID or Password is incorrect',
                    icon: 'error', // ✅ Default icon if not provided
                  }, disableClose: true,           // Prevents closing on outside click or ESC
                  hasBackdrop: true,            // Shows backdrop to block interaction
                  backdropClass: 'dialog-backdrop', // Optional: custom styling
                });
              }
            } else {
              this.dialogService.openDialog1({
                data: {
                  title: 'Error',
                  message: 'User ID or Password is incorrect',
                  icon: 'error', // ✅ Default icon if not provided
                }, disableClose: true,           // Prevents closing on outside click or ESC
                hasBackdrop: true,            // Shows backdrop to block interaction
                backdropClass: 'dialog-backdrop', // Optional: custom styling
              });
            }
          }
        }
      },
      error: () => {
        console.log('login failed');
        this.isLoading.set(false);
      }
    });
  }
}

